import type { Episode } from "@/hooks/useEpisodes";
import { theme } from "@/lib/theme";

interface EpisodeModalProps {
  episode: Episode;
  onClose: () => void;
}

export default function EpisodeModal({ episode, onClose }: EpisodeModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[90]" onClick={onClose}>
      <div className="relative w-full max-w-3xl" onClick={event => event.stopPropagation()}>
        <div className="absolute inset-0 bg-gray-900 dark:bg-gray-700 rounded-xl translate-y-2 translate-x-2" />

        <div className={`relative z-10 ${theme.cards.episode} p-4 sm:p-8 max-h-[calc(100vh-6rem)] sm:max-h-[90vh] overflow-y-auto`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-2xl font-bold text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400 transition-colors"
            aria-label="Close modal"
          >
            ×
          </button>

          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white pr-12">{episode.title}</h2>

          <div className="prose prose-lg mb-6 text-gray-700 dark:text-gray-300 whitespace-pre-line max-h-[40vh] overflow-y-auto pr-4">
            {episode.description}
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <h3 className="font-bold mb-2 text-gray-900 dark:text-white">Format</h3>
              <div className="flex flex-wrap gap-2">
                {episode.formatTags[0] && (
                  <span className={`px-3 py-1.5 ${theme.tags.format} rounded-full text-sm font-bold text-center min-w-[180px] md:min-w-[220px] inline-block`}>
                    {episode.formatTags[0]}
                  </span>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-2 text-gray-900 dark:text-white">Themes</h3>
              <div className="flex flex-wrap gap-2">
                {episode.themeTags.map(tag => (
                  <span key={tag} className={`px-3 py-1.5 ${theme.tags.theme} rounded-full text-sm text-center min-w-[180px] md:min-w-[220px] inline-block`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-2 text-gray-900 dark:text-white">Topics</h3>
              <div className="flex flex-wrap gap-2">
                {episode.topicTags.map(tag => (
                  <span key={tag} className={`px-3 py-1.5 ${theme.tags.topic} rounded-full text-sm text-center min-w-[180px] md:min-w-[220px] inline-block`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {episode.audioUrl ? (
            <a
              href={episode.audioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-6 py-2 ${theme.tags.format} rounded-lg font-bold hover:-translate-y-1 transition-transform`}
              onClick={event => event.stopPropagation()}
            >
              Listen
            </a>
          ) : (
            <span className="inline-block px-6 py-2 bg-gray-300 dark:bg-gray-600 border-3 border-gray-900 dark:border-gray-700 rounded-lg font-bold text-gray-600 dark:text-gray-300 cursor-not-allowed">
              No Audio Available
            </span>
          )}

          <div className="block sm:hidden mt-6">
            <button
              onClick={onClose}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
            >
              Close Episode
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
